import LazyTable, {
  BasicCellType,
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import useGlobalContext from "hooks/globalData";
import { useIntl } from "react-intl";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  ListRequestOfBaseFilter,
  Question,
  ResponseOfEvaluation,
  SkuEvaluation,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { IconButton, Tooltip } from "@mui/material";
import { useState } from "react";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import EvaluationPreview from "components/EvaluationPreview";

export interface SkuEvaluationModel extends SkuEvaluation {
  id: string;
  skuAssociation: string;
}

type SkuEvaluationsListProps = {
  skuId: string;
};

const SkuEvaluationsList = ({ skuId }: SkuEvaluationsListProps) => {
  const navigate = useNavigate();
  const { formatMessage: t } = useIntl();
  const {
    evaluationService,
    evaluationDetailsUrl,
    skuDetailsUrl,
  } = useGlobalContext();
  const { organizationId } = contextStore.useState();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const { data, isFetching } = useQuery(
    [
      "skuEvaluations",
      skuId,
      organizationId,
      page,
      rowsPerPage,
      orderBy,
      order,
    ],
    () =>
      evaluationService.getSkuEvaluations(
        skuId,
        ListRequestOfBaseFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
        })
      )
  );

  const [evaluationIdForPreview, setEvaluationIdForPreview] = useState<
    string | null
  >(null);

  const rows = (data?.data ?? []).map(
    (e) =>
      ({
        ...e,
        id: e.evaluationId,
        skuAssociation: t({
          id: e.isDirectSkuAssociation ? "directLabel" : "indirectLabel",
        }),
      } as SkuEvaluationModel)
  );

  const {
    isLoading: isEvaluationPreviewLoading,
    isError: isEvaluationPreviewLoadError,
    data: evaluationDetailsResponse,
  } = useQuery<ResponseOfEvaluation>(
    ["evaluation", evaluationIdForPreview],
    () => evaluationService.getEvaluationById(evaluationIdForPreview!),
    {
      enabled: !!evaluationIdForPreview,
    }
  );

  const evaluationDetails = evaluationDetailsResponse?.data;

  const PreviewButton = ({ value }: { value: SkuEvaluationModel }) => {
    return (
      <Tooltip title={t({ id: "previewEvaluationLabel" })}>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setEvaluationIdForPreview(value.evaluationId!);
          }}
          disabled={isEvaluationPreviewLoading}
        >
          <VisibilityIcon />
        </IconButton>
      </Tooltip>
    );
  };

  const baseCell = {
    align: "left",
    width: 0,
    type: "text",
  } as BasicCellType;

  const cells: Array<TableCellType> = [
    {
      ...baseCell,
      accessor: "organizationName",
      label: t({ id: "organization" }),
    },
    {
      ...baseCell,
      accessor: "name",
      label: t({ id: "nameLabel" }),
    },
    {
      ...baseCell,
      accessor: "type",
      label: t({ id: "typeLabel" }),
    },
    {
      ...baseCell,
      accessor: "description",
      label: t({ id: "descriptionLabel" }),
    },
    {
      ...baseCell,
      accessor: "isDirectSkuAssociation",
      label: t({ id: "skuAssociationLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 0,
      type: "node",
      View: PreviewButton,
      noSort: true,
      label: t({ id: "actions" }),
    },
  ];

  const onClick = (id: string, row: any) => {
    navigate(evaluationDetailsUrl(row.evaluationId), {
      state: {
        backButtonProps: {
          redirectRoute: skuDetailsUrl(skuId),
          labelKey: "backToSkuButtonLabel",
        },
      },
    });
  };

  return (
    <>
      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows,
          loading: isFetching,
          noResultsLabel: t({ id: "noEvaluationsLabel" }),
          allowPagination: rows.length !== 0 && !isFetching,
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => evaluationDetailsUrl(id)),
          ],
        }}
      />
      {evaluationDetails && (
        <EvaluationPreview
          title={evaluationDetails.name}
          questions={
            (evaluationDetails.evaluationQuestions?.map(
              (x) => x.question
            ) as Question[]) ?? []
          }
          onClose={() => setEvaluationIdForPreview(null)}
          open={
            evaluationIdForPreview !== null &&
            !isEvaluationPreviewLoading &&
            !isEvaluationPreviewLoadError
          }
        />
      )}
    </>
  );
};

export default SkuEvaluationsList;
